import Vue from "vue";
import VueRouter from "vue-router";
import beforeEach from "./beforeEach";

Vue.use(VueRouter);

const routes = [
  {
    path: "/",
    redirect: "/home",
  },
  {
    path: "/user-not-linked",
    name: "UserNotLinked",
    component: () => import("../views/userNotLinked/UserNotLinked.vue"),
  },
  {
    path: "/auth",
    name: "Auth",
    meta: { preventAuth: true },
    component: () =>
      import(/* webpackChunkName: "auth" */ "../views/auth/Auth.vue"),
  },
  {
    path: "/home",
    name: "Home",
    meta: { requiresAuth: true },
    component: () =>
      import(/* webpackChunkName: "home" */ "../views/home/Home.vue"),
  },
  {
    path: "/omnichannel",
    name: "OmniChannel",
    meta: { requiresAuth: true },
    // Piloto de multiatendimento: a tela unificada só está disponível para quem
    // pode conduzir mais de um atendimento, e quem não estiver no piloto é
    // mandado para a fila em tabela. O desvio mora no `beforeEach`, que decide
    // depois de recarregar a sessão — um `beforeEnter` aqui rodaria com o
    // usuário defasado do localStorage no F5. Guard temporário — ver ADR-008.
    component: () =>
      import(
        /* webpackChunkName: "omniChannel" */ "../views/omnichannel/OmniChannel.vue"
      ),
  },
  {
    path: "/counters",
    meta: {
      requiresAuth: true,
      requiresPermission: "desks",
    },
    component: () =>
      import(
        /* webpackChunkName: "counters" */ "../views/counters/Counters.vue"
      ),
    children: [
      {
        path: "",
        name: "Counters",
        meta: { requiresAuth: true, requiresPermission: "desks" },
        component: () =>
          import(
            /* webpackChunkName: "counters-children" */ "../views/counters/children/counters/Counters.vue"
          ),
      },
      {
        path: "cabinets",
        name: "Cabinets",
        meta: { requiresAuth: true, requiresPermission: "desks" },
        component: () =>
          import(
            /* webpackChunkName: "counters-children" */ "../views/counters/children/cabinets/Cabinets.vue"
          ),
      },
      {
        path: "courts",
        name: "Courts",
        meta: { requiresAuth: true, requiresPermission: "subgroups" },
        component: () =>
          import(
            /* webpackChunkName: "courts" */ "../views/counters/children/courts/Courts.vue"
          ),
      },
      {
        path: "jurisdictions",
        name: "Jurisdictions",
        meta: {
          requiresAuth: true,
          requiresPermission: "groups",
        },
        component: () =>
          import(
            /* webpackChunkName: "jurisdictions" */ "../views/counters/children/jurisdictions/Jurisdictions.vue"
          ),
      },
    ],
  },
  {
    path: "/schedules-queue",
    meta: {
      requiresAuth: true,
      requiresPermission: "appointments.queue",
    },
    component: () =>
      import(
        /* webpackChunkName: "schedules-queue" */ "../views/schedulesQueue/SchedulesQueue.vue"
      ),
    redirect: "/schedules-queue/waiting",
    children: [
      {
        path: "waiting",
        name: "SchedulesQueueWaiting",
        meta: { requiresAuth: true, requiresPermission: "appointments.queue" },
        component: () =>
          import(
            /* webpackChunkName: "schedules-queue-waiting" */ "../views/schedulesQueue/children/waiting/Waiting.vue"
          ),
      },
      {
        path: "in-progress",
        name: "SchedulesQueueInProgress",
        meta: { requiresAuth: true, requiresPermission: "appointments.queue" },
        component: () =>
          import(
            /* webpackChunkName: "schedules-queue-in-progress" */ "../views/schedulesQueue/children/in-progress/InProgress.vue"
          ),
      },
    ],
  },
  {
    path: "/schedules-reports",
    name: "SchedulesReports",
    meta: {
      requiresAuth: true,
      requiresPermission: "reports",
    },
    component: () =>
      import(
        /* webpackChunkName: "reports" */ "../views/schedulesReports/SchedulesReports.vue"
      ),
    children: [
      {
        path: "",
        name: "ScheduleQueueAttendance",
        meta: { requiresAuth: true, requiresPermission: "reports" },
        component: () =>
          import(
            /* webpackChunkName: "queue-attendance" */
            "../views/schedulesReports/children/queueAttendance/QueueAttendance.vue"
          ),
      },
      {
        path: "time-attendance",
        name: "ScheduleTimeAttendance",
        meta: { requiresAuth: true, requiresPermission: "reports" },
        component: () =>
          import(
            /* webpackChunkName: "time-attendance" */ "../views/schedulesReports/children/timeAttendance/TimeAttendance.vue"
          ),
      },
      {
        path: "satisfaction",
        name: "ScheduleSatisfaction",
        meta: { requiresAuth: true, requiresPermission: "reports" },
        component: () =>
          import(
            /* webpackChunkName: "satisfaction" */ "../views/schedulesReports/children/satisfaction/Satisfaction.vue"
          ),
      },
    ],
  },
  {
    path: "/schedules",
    name: "Schedules",
    meta: {
      requiresAuth: true,
      requiresPermission: "appointments",
    },
    component: () =>
      import(
        /* webpackChunkName: "schedules" */ "../views/schedules/Schedules.vue"
      ),
  },
  {
    path: "/reports",
    name: "Reports",
    meta: {
      requiresAuth: true,
      requiresPermission: "reports",
    },
    component: () =>
      import(/* webpackChunkName: "reports" */ "../views/reports/Reports.vue"),
    children: [
      {
        path: "",
        name: "QueueAttendance",
        meta: { requiresAuth: true, requiresPermission: "reports" },
        component: () =>
          import(
            /* webpackChunkName: "queue-attendance" */
            "../views/reports/children/queueAttendance/QueueAttendance.vue"
          ),
      },
      {
        path: "consolidated-queue-attendance",
        name: "ConsolidatedQueueAttendance",
        meta: { requiresAuth: true, requiresPermission: "reports" },
        component: () =>
          import(
            /* webpackChunkName: "consolidated-queue-attendance" */
            "../views/reports/children/cosolidatedQueueAttendance/ConsolidatedQueueAttendance.vue"
          ),
      },
      {
        path: "time-attendance",
        name: "TimeAttendance",
        meta: { requiresAuth: true, requiresPermission: "reports" },
        component: () =>
          import(
            /* webpackChunkName: "time-attendance" */ "../views/reports/children/timeAttendance/TimeAttendance.vue"
          ),
      },
      {
        path: "satisfaction",
        name: "Satisfaction",
        meta: { requiresAuth: true, requiresPermission: "reports" },
        component: () =>
          import(
            /* webpackChunkName: "satisfaction" */ "../views/reports/children/satisfaction/Satisfaction.vue"
          ),
      },
      {
        path: "queue-quit",
        name: "Quit",
        meta: { requiresAuth: true, requiresPermission: "reports" },
        component: () =>
          import(
            /* webpackChunkName: "quit" */ "../views/reports/children/quit/Quit.vue"
          ),
      },
      {
        path: "queue-waiting-time",
        name: "QueueWaitingTime",
        meta: { requiresAuth: true, requiresPermission: "reports" },
        component: () =>
          import(
            /* webpackChunkName: "queue-waiting-time" */ "../views/reports/children/queueWaitingTime/QueueWaitingTime.vue"
          ),
      },
    ],
  },
  {
    path: "/users",
    name: "Users",
    meta: {
      requiresAuth: true,
      requiresPermission: "people",
    },
    component: () =>
      import(/* webpackChunkName: "users" */ "../views/users/Users.vue"),
  },
  {
    path: "/attendance",
    meta: { requiresAuth: true, requiresPermission: "attendances" },
    component: () =>
      import(
        /* webpackChunkName: "attendance" */ "../views/attendance/Attendance.vue"
      ),
    children: [
      {
        path: ":counterIDs?",
        name: "Queue",
        redirect: "/attendance/:counterIDs?/waiting",
        meta: { requiresAuth: true, requiresPermission: "attendances" },
        component: () =>
          import(
            /* webpackChunkName: "queue" */ "../views/attendance/children/queue/Queue.vue"
          ),
        children: [
          {
            path: "waiting",
            name: "Waiting",
            meta: { requiresAuth: true, requiresPermission: "attendances" },
            component: () =>
              import(
                /* webpackChunkName: "waiting-children" */
                "../views/attendance/children/queue/children/waiting/Waiting.vue"
              ),
          },
          {
            path: "in-progress",
            name: "InProgress",
            meta: { requiresAuth: true, requiresPermission: "attendances" },
            component: () =>
              import(
                /* webpackChunkName: "in-progress" */
                "../views/attendance/children/queue/children/inProgress/InProgress.vue"
              ),
          },
          {
            path: "online-attendances",
            name: "OnlineAttendances",
            meta: { requiresAuth: true, requiresPermission: "attendances" },
            component: () =>
              import(
                /* webpackChunkName: "in-progress" */
                "../views/attendance/children/queue/children/onlineAttendances/OnlineAttendances.vue"
              ),
          },
        ],
      },
      {
        path: "chat/:attendanceID",
        name: "Chat",
        meta: { requiresAuth: true },
        component: () =>
          import(
            /* webpackChunkName: "chat" */ "../views/attendance/children/chat/Chat.vue"
          ),
      },
    ],
  },
  {
    path: "/calendar/:calendarID/:type?/:deskID?",
    name: "Calendar",
    meta: {
      requiresAuth: true,
      requiresPermission: "desks",
    },
    component: () =>
      import(
        /* webpackChunkName: "calendar" */ "../views/calendar/Calendar.vue"
      ),
  },
  {
    path: "/audiences",
    component: () =>
      import(
        /* webpackChunkName: "audiences" */ "../views/audiences/Audiences.vue"
      ),
    meta: {
      requiresAuth: true,
      requiresPermission: "audiences",
    },
    children: [
      {
        path: "",
        name: "Audiences",
        meta: {
          requiresAuth: true,
          requiresPermission: "audiences",
        },
        component: () =>
          import(
            /* webpackChunkName: "audiences-children" */ "../views/audiences/children/audiences/Audiences.vue"
          ),
      },
      {
        path: "details/:id",
        name: "AudienceDetails",
        meta: {
          requiresAuth: true,
          requiresPermission: "audiences",
        },
        component: () =>
          import(
            /* webpackChunkName: "audience-details" */
            "../views/audiences/children/audienceDetails/AudienceDetails.vue"
          ),
      },
      {
        path: "chat",
        name: "AudienceChat",
        meta: {
          requiresAuth: true,
          requiresPermission: "audiences",
        },
        component: () =>
          import(
            /* webpackChunkName: "audience-chat" */ "../views/audiences/children/audienceChat/AudienceChat.vue"
          ),
      },
      {
        path: "diagnostics",
        name: "AudienceDiagnostics",
        meta: {
          requiresAuth: true,
          requiresPermission: "audiences",
        },
        component: () =>
          import(
            /* webpackChunkName: "audience-diagnostics" */
            "../views/audiences/children/diagnostics/Diagnostics.vue"
          ),
      },
      {
        path: "import",
        name: "ImportAudience",
        meta: {
          requiresAuth: true,
          requiresPermission: "audiences",
        },
        component: () =>
          import(
            /* webpackChunkName: "audience-chat" */ "../views/audiences/children/importAudience/ImportAudience.vue"
          ),
      },
    ],
  },
  {
    path: "/entities",
    meta: {
      requiresAuth: true,
      requiresPermission: "entities",
    },
    component: () =>
      import(
        /* webpackChunkName: "entities" */ "../views/entities/Entities.vue"
      ),
    children: [
      {
        path: "",
        name: "Entities",
        meta: {
          requiresAuth: true,
          requiresPermission: "audiences",
        },
        component: () =>
          import(
            /* webpackChunkName: "entities-children" */ "../views/entities/children/entities/Entities.vue"
          ),
      },
      {
        path: "waiting-room/:id",
        name: "WaitingRoom",
        meta: {
          requiresAuth: true,
          requiresPermission: "audiences",
        },
        component: () =>
          import(
            /* webpackChunkName: "waiting-room" */ "../views/entities/children/waitingRoom/WaitingRoom.vue"
          ),
      },
    ],
  },
  // Pré-textos são sempre de uma unidade: a tela é aberta pelo menu de ações
  // da cidade, do fórum ou do balcão, em Balcões.
  {
    path: "/pre-texts",
    redirect: "/counters",
  },
  {
    path: "/pre-texts/:unitType(comarca|vara|desk)/:unitID",
    name: "PreTexts",
    meta: {
      requiresAuth: true,
      requiresPermission: "pre-texts",
    },
    component: () =>
      import(
        /* webpackChunkName: "pre-texts" */ "../views/preTexts/PreTexts.vue"
      ),
  },
  {
    path: "/settings",
    name: "Settings",
    meta: {
      requiresAuth: true,
      requiresPermission: "permission-groups",
    },
    component: () =>
      import(
        /* webpackChunkName: "settings" */ "../views/settings/Settings.vue"
      ),
  },
  {
    path: "*",
    redirect: "/home",
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach(beforeEach);

export default router;
